import {
  CustomStepCategoryEnum,
  FunnelInputTypeEnum,
  OfferIntents,
  OfferStages,
  OfferTypes,
  ResolveOfferIntents,
  StepTemplate,
} from '../../_models';

export const ReturnOfferIntents: OfferIntents[] = [
  OfferIntents.ReturnOrder,
  OfferIntents.ReturnSubscription,
  OfferIntents.ReturnTrial,
];

export enum OfferBuilderSection {
  Retention = 'retention',
  Returns = 'returns',
  Resolve = 'resolve',
}

// the Resolve section's route path differs from its slug (see getOfferBuilderRoot)
export const RESOLVE_ROUTE_PATH = 'resolve-paths';

export const OfferBuilderSectionNames: Record<OfferBuilderSection, string> = {
  [OfferBuilderSection.Retention]: 'Retention',
  [OfferBuilderSection.Returns]: 'Return',
  [OfferBuilderSection.Resolve]: 'Resolve',
};

// Retention is intentionally absent here - it is the default/fallback section for any
// intent not claimed by another section, so new sections only need an entry added here.
export const OfferBuilderSectionIntents: Partial<Record<OfferBuilderSection, OfferIntents[]>> = {
  [OfferBuilderSection.Returns]: ReturnOfferIntents,
  [OfferBuilderSection.Resolve]: ResolveOfferIntents,
};

export const TroubleshooterOfferIntents: OfferIntents[] = [
  OfferIntents.ReturnOrder,
  OfferIntents.ReturnSubscription,
  OfferIntents.ReturnTrial,
  OfferIntents.CancelSubscription,
  OfferIntents.CancelRebillHold,
  OfferIntents.Resolve,
  OfferIntents.ResolveSubscription,
];

export const steps: StepTemplate[] = [
  /* SURVEYS */
  {
    title: `${OfferStages[OfferIntents.Resolve]} Survey - Resolve`,
    description: 'Collect the reason a disputing customer wants a refund before issuing it.',
    type: CustomStepCategoryEnum.ResolveSurvey,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Survey,
    offer_intent: OfferIntents.Resolve,
  },
  {
    title: `${OfferStages[OfferIntents.ResolveSubscription]} Survey - Resolve`,
    description: 'Collect the reason a disputing subscriber wants a refund before issuing it and cancelling.',
    type: CustomStepCategoryEnum.ResolveSurvey,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Survey,
    offer_intent: OfferIntents.ResolveSubscription,
  },
  {
    title: `${OfferStages[OfferIntents.CancelSubscription]} Survey`,
    description: 'Collect feedback on Subscription cancellations. Multiple-choice for insights.',
    type: CustomStepCategoryEnum.CancelSubSurvey,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Survey,
    offer_intent: OfferIntents.CancelSubscription,
  },
  {
    title: `${OfferStages[OfferIntents.CancelSubscriptionHold]} Survey`,
    description: 'Collect feedback on Subscription cancellations. Multiple-choice for insights.',
    type: CustomStepCategoryEnum.CancelSubSurvey,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Survey,
    offer_intent: OfferIntents.CancelSubscriptionHold,
  },
  {
    title: `${OfferStages[OfferIntents.CancelRebillHold]} Survey`,
    description: 'Collect feedback on Subscription cancellations. Multiple-choice for insights.',
    type: CustomStepCategoryEnum.CancelSubSurvey,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Survey,
    offer_intent: OfferIntents.CancelRebillHold,
  },
  {
    title: `${OfferStages[OfferIntents.CancelTrialHold]} Survey`,
    description: 'Collect feedback on Trial cancellations. Multiple-choice for insights.',
    type: CustomStepCategoryEnum.CancelTrialSurvey,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Survey,
    offer_intent: OfferIntents.CancelTrialHold,
  },
  {
    title: `${OfferStages[OfferIntents.CancelTrial]} Survey`,
    description: 'Collect feedback on Trial cancellations. Multiple-choice for insights.',
    type: CustomStepCategoryEnum.CancelTrialSurvey,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Survey,
    offer_intent: OfferIntents.CancelTrial,
  },
  {
    title: `${OfferStages[OfferIntents.CancelOrder]} Survey`,
    description: 'Collect feedback on Order cancellations. Multiple-choice for insights.',
    type: CustomStepCategoryEnum.CancelOrderSurvey,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Survey,
    offer_intent: OfferIntents.CancelOrder,
  },
  {
    title: `${OfferStages[OfferIntents.ReturnOrder]} Survey`,
    description: 'Gather feedback on return requests. Multiple-choice for insights.',
    type: CustomStepCategoryEnum.ReturnSurvey,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Survey,
    offer_intent: OfferIntents.ReturnOrder,
  },
  {
    title: `${OfferStages[OfferIntents.ReturnSubscription]} Survey`,
    description: 'Gather feedback on subscription return requests. Multiple-choice for insights.',
    type: CustomStepCategoryEnum.ReturnSurvey,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Survey,
    offer_intent: OfferIntents.ReturnSubscription,
  },
  {
    title: `${OfferStages[OfferIntents.ReturnTrial]} Survey`,
    description: 'Gather feedback on trial return requests. Multiple-choice for insights.',
    type: CustomStepCategoryEnum.ReturnSurvey,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Survey,
    offer_intent: OfferIntents.ReturnTrial,
  },
  {
    title: 'Basic Survey',
    description: 'Standard survey for customer feedback. Various response options.',
    type: CustomStepCategoryEnum.Survey,
    icon: 'bacic-survey.svg',
    offer_type: OfferTypes.Survey,
    disabled: true,
  },
  {
    title: 'Feedback',
    description: 'Encourage opinions and comments through a feedback survey.',
    type: CustomStepCategoryEnum.Feedback,
    icon: 'feedback.svg',
    offer_type: OfferTypes.Survey,
    disabled: true,
  },

  /* DOWNSELLS */
  {
    title: 'Discount Offer',
    description: 'Offer with future discount to encourage subscription continuation',
    type: CustomStepCategoryEnum.DiscountFutureOrder,
    icon: 'discount-future.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [
      OfferIntents.CancelSubscriptionHold,
      OfferIntents.CancelRebillHold,
      OfferIntents.CancelTrialHold,
      OfferIntents.CancelSubscription,
      OfferIntents.CancelTrial,
    ],
  },
  {
    title: 'Partial Refund',
    description: 'Partial refund downsell for customers canceling their order.',
    type: CustomStepCategoryEnum.RefundOrder,
    icon: 'discount-refund.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [
      OfferIntents.CancelOrder,
      OfferIntents.CancelSubscriptionHold,
      OfferIntents.CancelRebillHold,
    ],
  },
  {
    title: 'Offer Details',
    description: 'Add details to help customers understand the promotion or discount being offered.',
    type: CustomStepCategoryEnum.OfferDetails,
    icon: 'offer-details.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [
      OfferIntents.CancelOrder,
      OfferIntents.CancelSubscriptionHold,
      OfferIntents.CancelRebillHold,
      OfferIntents.CancelTrialHold,
      OfferIntents.CancelSubscription,
      OfferIntents.CancelTrial,
      OfferIntents.ReturnOrder,
      OfferIntents.ReturnSubscription,
      OfferIntents.ReturnTrial,
      OfferIntents.Resolve,
      OfferIntents.ResolveSubscription,
    ],
  },
  {
    title: 'Pause Offer',
    description: 'Offer allowing customers to temporarily pause subscriptions as an alternative to cancellation.',
    type: CustomStepCategoryEnum.PauseSub,
    icon: 'pause-subscription.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [
      OfferIntents.CancelSubscriptionHold,
      OfferIntents.CancelSubscription,
      OfferIntents.CancelRebillHold,
    ],
    subtypes: [
      {
        title: 'Pause Subscription (Flexible)',
        description: 'Let customers choose from multiple pause durations.',
        type: CustomStepCategoryEnum.PauseSurvey,
        icon: 'pause-sub-flexible.svg',
        offer_type: OfferTypes.Downsell,
      },
      {
        title: 'Fixed Pause Period',
        description: 'Apply a preset pause duration as a step in the flow.',
        type: CustomStepCategoryEnum.PauseSub,
        icon: 'pause-subscription.svg',
        offer_type: OfferTypes.Downsell,
      },
    ],
  },
  {
    title: 'Extend Trial',
    description: 'Encourage extension of trial period through a downsell offer.',
    type: CustomStepCategoryEnum.ExtendTrial,
    icon: 'extend-trial.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [OfferIntents.CancelTrialHold, OfferIntents.CancelTrial],
  },
  {
    title: 'Return Downsell',
    description: 'Tailored downsell for customers initiating returns, providing a partial refund.',
    type: CustomStepCategoryEnum.ReturnOrderRMA,
    icon: 'return-downsell.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [
      OfferIntents.ReturnOrder,
      OfferIntents.ReturnSubscription,
      OfferIntents.ReturnTrial,
      OfferIntents.Resolve,
      OfferIntents.ResolveSubscription,
    ],
  },
  {
    title: 'Shipping Frequency Offer',
    description: 'Allow customers to receive their subscription less often instead of canceling.',
    type: CustomStepCategoryEnum.AdjustShippingFrequency,
    icon: 'shipping-frequency.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [
      OfferIntents.CancelSubscriptionHold,
      OfferIntents.CancelRebillHold,
      OfferIntents.CancelSubscription,
    ],
  },
  {
    title: 'Product Swaps',
    description: 'Let customers switch to a different product instead of canceling.',
    type: CustomStepCategoryEnum.ReviseOrder,
    icon: 'swap-products.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [
      OfferIntents.CancelOrder,
      OfferIntents.CancelSubscriptionHold,
      OfferIntents.CancelRebillHold,
      OfferIntents.CancelSubscription,
    ],
    subtypes: [
      {
        title: 'Bundle Swap',
        description: 'Let customers move between bundle sizes or bundle tiers within the same product family.',
        type: CustomStepCategoryEnum.ReviseOrder,
        icon: 'bundle-swap.svg',
        offer_type: OfferTypes.Downsell,
      },
      {
        title: 'Swap Variant',
        description: 'Let customers switch flavors, sizes, colors, or shades without changing their subscription.',
        type: CustomStepCategoryEnum.ChangeVariant,
        icon: 'variant-swap.svg',
        offer_type: OfferTypes.Downsell,
      },
      {
        title: 'Related Product Swap',
        description: "Offer a different but related product when the original one isn't a good fit.",
        type: null,
        icon: 'swap-products.svg',
        offer_type: OfferTypes.Downsell,
        disabled: true,
      },
      {
        title: 'Upgrade',
        description: 'Move the customer to a higher value product or bundle for better results or more value.',
        type: null,
        icon: 'upgrade-swap.svg',
        offer_type: OfferTypes.Downsell,
        disabled: true,
      },
      {
        title: 'Downgrade',
        description: 'Switch the customer to a lower priced or smaller option to reduce cost or quantity.',
        type: null,
        icon: 'downgrade-swap.svg',
        offer_type: OfferTypes.Downsell,
        disabled: true,
      },
      {
        title: 'Swap + Incentive',
        description: 'Combine a product swap with credit, discount, or free item to increase acceptance.',
        type: null,
        icon: 'swap-incentive.svg',
        offer_type: OfferTypes.Downsell,
        disabled: true,
      },
    ],
  },
  {
    title: 'Reactivate Offer',
    description: 'Win Back cancelled customers with a targeted offer to restart their subscription.',
    type: CustomStepCategoryEnum.ReActivateSubscriptionWithDiscount,
    icon: 'reactivate-offer.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [
      OfferIntents.CancelSubscription,
    ],
  },
  {
    title: 'Troubleshooter',
    description: 'Route the customer through a troubleshooter flow to diagnose and resolve their issue.',
    type: FunnelInputTypeEnum.EnterTroubleshooter,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: TroubleshooterOfferIntents,
  },
  {
    title: 'Loss Aversion',
    description: 'Downsell using loss aversion strategy to prevent cancellations.',
    type: CustomStepCategoryEnum.LossAversion,
    icon: 'loss-aversion.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [
      OfferIntents.CancelOrder,
      OfferIntents.CancelSubscriptionHold,
      OfferIntents.CancelRebillHold,
      OfferIntents.CancelTrialHold,
      OfferIntents.CancelSubscription,
      OfferIntents.CancelTrial,
    ],
    disabled: true,
  },
  {
    title: 'Time for free',
    description: 'Downsell with free service or product usage for a specified period.',
    icon: 'time-for-free.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [
      OfferIntents.CancelSubscriptionHold,
      OfferIntents.CancelRebillHold,
      OfferIntents.CancelTrialHold,
      OfferIntents.CancelSubscription,
      OfferIntents.CancelTrial,
    ],
    disabled: true,
  },
  {
    title: 'Testimonial',
    description: 'Use customer testimonials in a downsell to build trust and influence positive decisions.',
    type: CustomStepCategoryEnum.Testimonial,
    icon: 'testimonial.svg',
    offer_type: OfferTypes.Downsell,
    offer_intents: [
      OfferIntents.CancelOrder,
      OfferIntents.CancelSubscriptionHold,
      OfferIntents.CancelRebillHold,
      OfferIntents.CancelTrialHold,
      OfferIntents.CancelSubscription,
      OfferIntents.CancelTrial,
    ],
    disabled: true,
  },

  /* UPSELLS */
  {
    title: 'Upsell',
    description: 'Offer more products to increase order value during support or checkout.',
    type: CustomStepCategoryEnum.UpsellOrder,
    icon: 'upsell.svg',
    offer_type: OfferTypes.Upsell,
  },
  {
    title: 'Discounted Upsell',
    description: 'Add a product at a discount or free to prevent cancellations.',
    type: CustomStepCategoryEnum.UpsellOrder,
    icon: 'discounted-upsell.svg',
    offer_type: OfferTypes.Upsell,
  },
  {
    title: 'Special Offer',
    description: 'Show limited-time deals to boost retention or quick sales.',
    icon: 'special-offer.svg',
    offer_type: OfferTypes.Upsell,
    disabled: true,
  },

  /* SUPPORTS */
  {
    title: 'Schedule Support',
    description: 'Support step enabling customers to schedule sessions for personalized assistance.',
    type: CustomStepCategoryEnum.ScheduleSupport,
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Support,
    disabled: true,
  },
  {
    title: 'Lifeline step',
    description: 'Offer a lifeline for immediate connection with live agent support in critical situations.',
    icon: 'lifeline.svg',
    offer_type: OfferTypes.Support,
    disabled: true,
  },
  {
    title: 'Hand-off',
    description: 'Smooth transition from automated service to live agent assistance for complex queries.',
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Support,
    disabled: true,
  },
  {
    title: 'Contact Form',
    description: 'Let customers share issues when stuck for better support.',
    icon: 'survey-icon.svg',
    offer_type: OfferTypes.Support,
    disabled: true,
  },

  /* ORDER OPTIONS */
  {
    title: 'Shipping Frequency Offer',
    description: 'Downsell related to shipping frequency, offering an alternative plan.',
    type: CustomStepCategoryEnum.AdjustShippingFrequency,
    icon: 'shipping-frequency.svg',
    offer_type: OfferTypes.CustomerPortal,
  },
  {
    title: 'Pause Options',
    description: 'Downsell allowing customers to temporarily pause subscriptions as an alternative to cancellation.',
    type: CustomStepCategoryEnum.PauseSurvey,
    icon: 'pause-subscription.svg',
    offer_type: OfferTypes.CustomerPortal,
  },
  {
    title: 'Swap Products',
    description: 'Downsell allowing customers to exchange or swap their product/package.',
    type: CustomStepCategoryEnum.ReviseOrder,
    icon: 'swap-products.svg',
    offer_type: OfferTypes.CustomerPortal,
  },
  {
    title: 'Re-Activate Subscription',
    description: 'Allow customers to easily resume a deactivated subscription.',
    type: CustomStepCategoryEnum.ReActivateSubscriptionWithDiscount,
    icon: 'reactivate-sub.svg',
    offer_type: OfferTypes.CustomerPortal,
  },
  {
    title: 'Offer Details',
    description: 'Communicate details of a special downsell offer, providing clear information and incentives.',
    type: CustomStepCategoryEnum.OfferDetails,
    icon: 'offer-details.svg',
    offer_type: OfferTypes.CustomerPortal,
  },
];
